import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaSearch, FaTimes } from "react-icons/fa";
import api from "../services/api";

const S = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');

.sb-wrapper {
  position: relative;
  width: 100%;
  font-family: 'DM Sans', sans-serif;
}

/* ─── Input row ─── */
.sb-input-row {
  display: flex;
  align-items: center;
  gap: 0;
  border: 1.5px solid #e8e4df;
  border-radius: 12px;
  background: #fff;
  transition: border-color 0.18s, box-shadow 0.18s;
  overflow: hidden;
}
.sb-input-row:focus-within {
  border-color: #1a5c38;
  box-shadow: 0 0 0 3px rgba(26,92,56,0.08);
}
.sb-icon {
  width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
  font-size: 0.88rem;
  flex-shrink: 0;
  pointer-events: none;
}
.sb-input {
  flex: 1;
  border: none;
  outline: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.92rem;
  color: #1a1a1a;
  background: transparent;
  padding: 11px 0;
}
.sb-input::placeholder { color: #bbb; }
.sb-clear {
  width: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
  font-size: 0.78rem;
  cursor: pointer;
  background: none;
  border: none;
  flex-shrink: 0;
  transition: color 0.13s;
}
.sb-clear:hover { color: #1a5c38; }

/* ─── Results dropdown ─── */
.sb-results {
  position: absolute;
  top: calc(100% + 6px);
  left: 0; right: 0;
  z-index: 1050;
  background: #fff;
  border: 1px solid #e8e4df;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.1);
  animation: sbSlide 0.16s ease;
}
@keyframes sbSlide {
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.sb-results-inner {
  max-height: 380px;
  overflow-y: auto;
  overflow-x: hidden;
}
.sb-results-inner::-webkit-scrollbar { width: 4px; }
.sb-results-inner::-webkit-scrollbar-track { background: transparent; }
.sb-results-inner::-webkit-scrollbar-thumb { background: #e8e4df; border-radius: 4px; }
.sb-results-inner::-webkit-scrollbar-thumb:hover { background: #1a5c38; }

/* ─── Each result item ─── */
.sb-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.13s;
  border-bottom: 1px solid #f5f5f5;
}
.sb-item:last-child { border-bottom: none; }
.sb-item:hover { background: #f9f7f4; }
.sb-item:hover .sb-item-name { color: #1a5c38; }

.sb-item-img-wrap {
  position: relative;
  width: 48px; height: 48px;
  border-radius: 10px;
  overflow: hidden;
  background: #f5f5f5;
  flex-shrink: 0;
  border: 1px solid #e8e4df;
}
.sb-item-img {
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform 0.2s;
}
.sb-item:hover .sb-item-img { transform: scale(1.06); }
.sb-sale-dot {
  position: absolute;
  top: 3px; right: 3px;
  background: #d94f3d; color: #fff;
  font-size: 0.58rem; font-weight: 700;
  padding: 1px 4px; border-radius: 4px;
  line-height: 1.3;
}

.sb-item-info { flex: 1; min-width: 0; }
.sb-item-name {
  font-size: 0.88rem; font-weight: 600; color: #1a1a1a;
  line-height: 1.3; margin-bottom: 2px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  transition: color 0.13s;
}
.sb-item-kr {
  font-size: 0.76rem; color: #aaa;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  margin-bottom: 4px;
}
.sb-item-prices { display: flex; align-items: center; gap: 6px; }
.sb-price { font-size: 0.88rem; font-weight: 700; color: #1a5c38; }
.sb-price-old { font-size: 0.76rem; color: #bbb; text-decoration: line-through; }

/* ─── Loading / empty states ─── */
.sb-state {
  padding: 18px 14px;
  text-align: center;
  font-size: 0.85rem;
  color: #aaa;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.sb-spinner {
  width: 14px; height: 14px;
  border: 2px solid #e8e4df;
  border-top-color: #1a5c38;
  border-radius: 50%;
  animation: sbspin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes sbspin { to { transform: rotate(360deg); } }

/* ─── Results footer ─── */
.sb-footer {
  padding: 8px 14px;
  border-top: 1px solid #f0ede9;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.sb-footer-count { font-size: 0.76rem; color: #aaa; }
.sb-footer-all {
  font-size: 0.78rem; font-weight: 600; color: #1a5c38;
  text-decoration: none; transition: opacity 0.13s;
}
.sb-footer-all:hover { opacity: 0.75; }
`;

const SearchBox = ({ onSelect }) => {
  const { t } = useTranslation();
  const [searchTerm,       setSearchTerm]       = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productImages,    setProductImages]    = useState({});
  const [allProducts,      setAllProducts]      = useState([]);
  const [showDropdown,     setShowDropdown]     = useState(false);
  const [loading,          setLoading]          = useState(false);

  const navigate          = useNavigate();
  const searchWrapperRef  = useRef(null);
  const inputRef          = useRef(null);

  // Fetch all products once
  useEffect(() => {
    api.get("/products")
      .then(res => {
        const products = Array.isArray(res.data) ? res.data : res.data.products || [];
        setAllProducts(products);
      })
      .catch(err => console.error("Search product fetch error:", err));
  }, []);

  // Auto-focus input when rendered inside the search overlay
  useEffect(() => {
    if (inputRef.current) setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = e => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter products
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts([]);
      setShowDropdown(false);
      return;
    }
    const term    = searchTerm.toLowerCase();
    const results = allProducts
      .filter(p =>
        p.name?.toLowerCase().includes(term) ||
        (p.name_kr && p.name_kr.toLowerCase().includes(term))
      )
      .slice(0, 7);

    setFilteredProducts(results);
    setShowDropdown(results.length > 0 || searchTerm.trim().length > 0);

    if (results.length > 0) fetchProductImages(results);
  }, [searchTerm, allProducts]);

  const fetchProductImages = async (products) => {
    setLoading(true);
    try {
      const imagePromises = products.map(async product => {
        if (product.imageUrl) return { id: product._id, imageUrl: product.imageUrl };
        try {
          const res    = await api.get(`/product-images/${product._id}`);
          const images = Array.isArray(res.data) ? res.data : [];
          const main   = images.find(img => img.is_main) || images[0];
          return { id: product._id, imageUrl: main?.url || "/images/default-product.jpg" };
        } catch {
          return { id: product._id, imageUrl: "/images/default-product.jpg" };
        }
      });
      const images   = await Promise.all(imagePromises);
      const imageMap = images.reduce((acc, { id, imageUrl }) => ({ ...acc, [id]: imageUrl }), {});
      setProductImages(prev => ({ ...prev, ...imageMap }));
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (productId) => {
    setSearchTerm("");
    setFilteredProducts([]);
    setShowDropdown(false);
    navigate(`/product/${productId}`);
    onSelect?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchTerm.trim()) {
      setShowDropdown(false);
      navigate(`/shop?search=${encodeURIComponent(searchTerm.trim())}`);
      onSelect?.();
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setFilteredProducts([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const getPrice      = p => p.is_on_sale ? p.sale_price : p.sell_price;
  const getSalePercent = p => {
    if (!p.is_on_sale) return 0;
    const orig = p.compare_price || p.sell_price;
    if (orig && p.sale_price && orig > p.sale_price) {
      return Math.round(((orig - p.sale_price) / orig) * 100);
    }
    return 0;
  };

  return (
    <>
      <style>{S}</style>
      <div className="sb-wrapper" ref={searchWrapperRef}>

        {/* Input */}
        <div className="sb-input-row">
          <div className="sb-icon"><FaSearch /></div>
          <input
            ref={inputRef}
            className="sb-input"
            type="text"
            placeholder={t("search.placeholder")}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onFocus={() => { if (filteredProducts.length > 0) setShowDropdown(true); }}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
          {searchTerm && (
            <button className="sb-clear" onClick={clearSearch} aria-label="Clear search">
              <FaTimes />
            </button>
          )}
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <div className="sb-results">
            <div className="sb-results-inner">
              {loading && filteredProducts.length === 0 && (
                <div className="sb-state">
                  <div className="sb-spinner" /> {t("search.searching")}
                </div>
              )}

              {!loading && filteredProducts.length === 0 && searchTerm.trim() && (
                <div className="sb-state">{t("search.noResults", { term: searchTerm })}</div>
              )}

              {filteredProducts.map(product => {
                const price       = getPrice(product);
                const oldPrice    = product.is_on_sale ? (product.compare_price || product.sell_price) : null;
                const salePct     = getSalePercent(product);
                const imgSrc      = productImages[product._id] || product.imageUrl || "/images/default-product.jpg";

                return (
                  <div
                    key={product._id}
                    className="sb-item"
                    onClick={() => handleSelect(product._id)}
                  >
                    <div className="sb-item-img-wrap">
                      <img
                        src={imgSrc}
                        alt={product.name}
                        className="sb-item-img"
                        onError={e => { e.target.src = "/images/default-product.jpg"; }}
                      />
                      {salePct > 0 && (
                        <span className="sb-sale-dot">-{salePct}%</span>
                      )}
                    </div>

                    <div className="sb-item-info">
                      <div className="sb-item-name">{product.name}</div>
                      {product.name_kr && (
                        <div className="sb-item-kr">{product.name_kr}</div>
                      )}
                      <div className="sb-item-prices">
                        <span className="sb-price">₩{price?.toLocaleString()}</span>
                        {oldPrice && (
                          <span className="sb-price-old">₩{oldPrice.toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            {filteredProducts.length > 0 && (
              <div className="sb-footer">
                <span className="sb-footer-count">{filteredProducts.length} result{filteredProducts.length !== 1 ? "s" : ""}</span>
                <a
                  href={`/shop?search=${encodeURIComponent(searchTerm)}`}
                  className="sb-footer-all"
                  onClick={e => {
                    e.preventDefault();
                    navigate(`/shop?search=${encodeURIComponent(searchTerm)}`);
                    setShowDropdown(false);
                    onSelect?.();
                  }}
                >
                  View all results →
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SearchBox;